import React, {
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  Skeleton,
  Fade
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { APIService } from 'src/api/api';

import BusinessIcon from '@mui/icons-material/Business';
import PropaneTankIcon from '@mui/icons-material/PropaneTank';
import CommuteIcon from '@mui/icons-material/Commute';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';

import SalesChart from 'src/components/dashboard/SalesChart';
import StockInfo from 'src/components/dashboard/StockInfo';
import ConsumptionChart from 'src/components/dashboard/ConsumptionChart';
import VehicleGroupConsumptionChart from 'src/components/dashboard/VehicleGroupConsumptionChart';
import VehicleStatusChart from 'src/components/dashboard/VehicleStatusChart';
import VehicleUtilizationChart from 'src/components/dashboard/VehicleUtilizationChart';
import FleetAnalytics from 'src/components/dashboard/FleetAnalytics';
import ReconciliationSummary from 'src/components/dashboard/ReconciliationSummary';
import AnalyticsStatCard from 'src/components/dashboard/AnalyticsStatCard';
import ScreenHeader from 'src/common/ScreenHeader';
import { isFleetOnlyModeEnabled } from 'src/utils/appMode';
import { getFuelQuantity } from 'src/utils/getInitials';

const renderConsumptionValue = (litres, gallons, weight = 600) => {
  const formattedValue = getFuelQuantity(litres, gallons);
  const conversionStart = formattedValue.indexOf(' (');

  if (conversionStart === -1) {
    return <span style={{ fontWeight: weight }}>{formattedValue}</span>;
  }

  return (
    <>
      <span style={{ fontWeight: weight }}>
        {formattedValue.slice(0, conversionStart)}
      </span>
      <span style={{ fontWeight: 400 }}>
        {formattedValue.slice(conversionStart).replace('gallons', 'gal')}
      </span>
    </>
  );
};

const Dashboard = () => {
  const [cardData, setCardData] = useState(null);
  const { appSettings } = useSelector((state) => state.settings);
  const location = useLocation();
  const vmsEnabled = !!appSettings?.vehicle_management;
  const fleetManagementEnabled = !!appSettings?.fleet_management;
  const isFleetOnlyMode = isFleetOnlyModeEnabled(appSettings);
  const analyticsView = fleetManagementEnabled
    && new URLSearchParams(location.search).get('analytics') === 'fleet'
    ? 'fleet'
    : 'fuel';
  const hasCardData = !!cardData;

  const statCards = useMemo(
    () => ([
      {
        key: 'sites',
        title: 'Total Sites',
        value: cardData?.sites?.total || 0,
        metaItems: [
          {
            value: cardData?.sites?.active ?? 0,
            label: 'Active',
            color: '#10B981'
          },
          {
            value: cardData?.sites?.inactive ?? 0,
            label: 'Inactive',
            color: '#EF4444'
          }
        ],
        icon: (<BusinessIcon />),
        color: '#10B981'
      },
      {
        key: 'dispensers',
        title: 'Total Tanks',
        value: cardData?.dispensers?.total || 0,
        metaItems: [
          {
            value: cardData?.dispensers?.active ?? 0,
            label: 'Active',
            color: '#10B981'
          },
          {
            value: cardData?.dispensers?.inactive ?? 0,
            label: 'Inactive',
            color: '#EF4444'
          }
        ],
        icon: (<PropaneTankIcon />),
        color: '#3B82F6'
      },
      {
        key: 'vehicles',
        title: 'Fleet Size',
        value: cardData?.vehicles?.total || 0,
        metaItems: [
          {
            value: cardData?.vehicles?.active ?? 0,
            label: 'Active',
            color: '#10B981'
          },
          {
            value: cardData?.vehicles?.inactive ?? 0,
            label: 'Inactive',
            color: '#EF4444'
          }
        ],
        icon: (<CommuteIcon />),
        color: '#F59E0B'
      },
      {
        key: 'sale',
        title: "Today's Consumption",
        value: renderConsumptionValue(
          cardData?.sale?.today_sale,
          cardData?.sale?.today_sale_gallons,
          800
        ),
        metaItems: [
          {
            value: renderConsumptionValue(
              cardData?.sale?.last_7days_sale,
              cardData?.sale?.last_7days_sale_gallons
            ),
            label: '· Last 7 Days',
            color: '#10B981'
          }
        ],
        icon: (<PointOfSaleIcon />),
        color: '#2563EB'
      }
    ]),
    [cardData]
  );

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        const res = await APIService.getDashboardCount();
        if (isMounted && res.success) {
          setCardData(res.result);
        }
      } catch (error) {
        console.error('Dashboard error:', error);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <Box
      sx={{
        bgcolor: isFleetOnlyMode ? '#F3F8FC' : '#F9FAFB',
        minHeight: '100vh',
        pt: 1.5,
        pb: 8
      }}
    >
      <Container maxWidth="xl">
        <Box sx={{ mb: 1.5 }}>
          <ScreenHeader title="Dashboard" />
        </Box>

        {!isFleetOnlyMode && (!fleetManagementEnabled || analyticsView === 'fuel') && (
          <Fade in timeout={500}>
            <Box>
              <Grid container spacing={3}>
                {statCards.map((card) => (
                  <Grid item xs={12} sm={6} md={3} key={card.key}>
                    {hasCardData ? (
                      <AnalyticsStatCard {...card} />
                    ) : (
                      <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
                    )}
                  </Grid>
                ))}

                <Grid item xs={12} md={8}>
                  <Card
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      height: '100%',
                      minHeight: 500,
                      boxShadow: '0 4px 24px 0 rgba(0,0,0,0.06)'
                    }}
                  >
                    <SalesChart />
                  </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                  <StockInfo compact />
                </Grid>

                <Grid item xs={12}>
                  <ReconciliationSummary />
                </Grid>

                {vmsEnabled && (
                  <>
                    <Grid item xs={12} md={7} sx={{ display: 'flex' }}>
                      <VehicleStatusChart />
                    </Grid>
                    <Grid item xs={12} md={5} sx={{ display: 'flex' }}>
                      <VehicleUtilizationChart />
                    </Grid>
                  </>
                )}

                {vmsEnabled ? (
                  <>
                    <Grid
                      item
                      xs={12}
                      md={5}
                      sx={{
                        display: 'flex'
                      }}
                    >
                      <VehicleGroupConsumptionChart />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      md={7}
                      sx={{
                        display: 'flex'
                      }}
                    >
                      <ConsumptionChart />
                    </Grid>
                  </>
                ) : (
                  <Grid item xs={12}>
                    <Card sx={{ p: 2, borderRadius: 3 }}>
                      <ConsumptionChart />
                    </Card>
                  </Grid>
                )}

              </Grid>
            </Box>
          </Fade>
        )}

        {(fleetManagementEnabled && analyticsView === 'fleet') || isFleetOnlyMode ? (
          <Fade in timeout={500}>
            <Box>
              <FleetAnalytics />
            </Box>
          </Fade>
        ) : null}
      </Container>
    </Box>
  );
};

export default Dashboard;
